/**
 * A utility class for parsing CSV data with support for:
 * - Quoted fields containing commas
 * - Escaped quotes within quoted fields
 * - Different line endings (Windows/Unix)
 * - Proper whitespace handling
 */
export class CSVParser {
  /**
   * Parses a CSV string into an array of objects using the first row as headers
   * @param {string} csvText - The raw CSV text to parse
   * @param {Object} options - Parsing options
   * @param {boolean} options.hasHeaders - Whether to use first row as headers (default: true)
   * @param {boolean} options.trimHeaders - Whether to trim whitespace from headers (default: true)
   * @param {boolean} options.trimValues - Whether to trim whitespace from values (default: true)
   * @returns {Array<Object>} Array of objects where keys are headers and values are row values
   */
  static parse(csvText, options = {}) {
    const {
      hasHeaders = true,
      trimHeaders = true,
      trimValues = true,
    } = options;

    // Split into rows, handling both Windows and Unix line endings
    const rows = csvText.trim().split(/\r?\n/).map(row =>
      this.parseRow(row, { trim: trimValues })
    );

    if (!hasHeaders) {
      return rows;
    }

    const headers = rows.shift().map(header => {
      const processedHeader = trimHeaders ? header.trim() : header;
      // Downcase first letter while preserving rest of the string
      return processedHeader.charAt(0).toLowerCase() + processedHeader.slice(1);
    });

    return rows.map(row => {
      const obj = {};
      row.forEach((value, index) => {
        if (index < headers.length) {
          obj[headers[index]] = value;
        }
      });
      return obj;
    });
  }

  /**
   * Parses a single CSV row into an array of values
   * @param {string} line - The CSV line to parse
   * @param {Object} options - Parsing options
   * @param {boolean} options.trim - Whether to trim values (default: true)
   * @returns {string[]} Array of parsed values
   */
  static parseRow(line, { trim = true } = {}) {
    const result = [];
    let cell = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') { // Handle escaped quotes
          cell += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(trim ? cell.trim() : cell);
        cell = '';
      } else {
        cell += char;
      }
    }

    // Push the last cell
    result.push(trim ? cell.trim() : cell);
    return result;
  }

  /**
   * Parses a CSV file from a Google Sheets URL
   * @param {string} url - The Google Sheets CSV export URL
   * @param {Object} options - Parsing options (same as parse method)
   * @returns {Promise<Array<Object>>} Promise resolving to parsed CSV data
   */
  static async parseGoogleSheet(url, options = {}) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const text = await response.text();
      return this.parse(text, options);
    } catch (error) {
      console.error("Error fetching or parsing Google Sheet:", error);
      throw error;
    }
  }
}
